import _ from 'lodash';
import { swapMapEntries } from '@/trendData/itemStore.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

/**
 * In-memory store for fetched table results and chart-derived ranges. Like `itemRuntime.store.ts` these are
 * runtime-only and must never be persisted, but they are table-specific rather than shared across
 * item types, so they live in their own store.
 */

export interface TableValues {
  /** Aggregated series rendered by the table visualization */
  data?: unknown;
  /** Name of the signal being aggregated (the y-axis) */
  signalName?: string;
  /** Key of the statistic the table aggregates by */
  stat?: string;
  /** x-axis category labels */
  categories?: string[];
  /** Unit of the aggregated values, as reported by the table's value column */
  valueUnitOfMeasure?: string;
}

export type TableAxisExtremes = {
  min: number;
  max: number;
};

export interface TableRuntimeState {
  values: Partial<Record<string, TableValues>>;
  /** Separate from fetched data so chart updates do not notify data subscribers. */
  autoAxisExtremes: Partial<Record<string, TableAxisExtremes>>;
}

export interface TableRuntimeActions {
  /**
   * Records the results carried by a table-results payload. Merges into the table's entry so
   * fields absent from the update keep their previous values, matching the item-cursor writes the
   * table store used to do
   */
  setValues(
    id: string,
    payload: {
      tableData?: unknown;
      signalName?: string;
      stat?: { key: string; timeUnits: string };
      categories?: string[];
      valueUnitOfMeasure?: string;
    },
  ): void;
  /** Records the y-axis range the chart chose for the table while auto scaling */
  setAutoAxisExtremes(id: string, extremes: TableAxisExtremes): void;
  /** Discards an item's results and auto-scale range, for when its data is no longer valid to render */
  clearValues(id: string): void;
  /**
   * Moves entries to their swapped-in ids, mirroring the flux stores renaming items in place on
   * TREND_SWAP_ITEMS: the swapped-in item keeps rendering the previous values until its fetch
   * returns, and nothing is left keyed by an id that no longer exists.
   */
  swapValues(swaps: Record<string, string>): void;
  reset(): void;
}

export const initialState: TableRuntimeState = {
  values: {},
  autoAxisExtremes: {},
};

const STORE_NAME = 'TableRuntime';

const tableRuntimeActionsFactory: ActionsFactory<TableRuntimeState, TableRuntimeActions> = (set, get, resetState) => ({
  setValues: (id, payload) => {
    const update: TableValues = { data: payload.tableData, signalName: payload.signalName };
    if (payload.stat) {
      update.stat = payload.stat.key;
    }
    if (payload.categories) {
      update.categories = payload.categories;
    }
    if (payload.valueUnitOfMeasure !== undefined) {
      update.valueUnitOfMeasure = payload.valueUnitOfMeasure;
    }

    const current = get().values[id];
    const merged = { ...current, ...update };
    if (_.isEqual(current, merged)) {
      return;
    }
    set((draft) => {
      draft.values[id] = merged;
    });
  },

  setAutoAxisExtremes: (id, extremes) => {
    const current = get().autoAxisExtremes[id];
    if (current?.min === extremes.min && current?.max === extremes.max) {
      return;
    }
    set((draft) => {
      draft.autoAxisExtremes[id] = extremes;
    });
  },

  clearValues: (id) => {
    if (!(id in get().values) && !(id in get().autoAxisExtremes)) {
      return;
    }
    set((draft) => {
      delete draft.values[id];
      delete draft.autoAxisExtremes[id];
    });
  },

  swapValues: (swaps) => {
    if (!Object.keys(swaps).some((id) => id in get().values || id in get().autoAxisExtremes)) {
      return;
    }
    set((draft) => {
      swapMapEntries(draft.values, swaps);
      swapMapEntries(draft.autoAxisExtremes, swaps);
    });
  },

  reset: resetState,
});

const [TableRuntimeStoreProvider, useTableRuntimeStoreInstance, useTableRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  tableRuntimeActionsFactory,
);

export { TableRuntimeStoreProvider };

export const useTableValues = () => useTableRuntimeSelector((state) => state.values);

export const useTableRuntimeActions = () => useTableRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateTableRuntimeStore(initialStateOverrides?: Partial<TableRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    tableRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
